import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { getSocket } from '../lib/socket';

export type SignalSeverity = 'high' | 'medium' | 'low';

export type SignalKind =
  | 'overdue_ar'
  | 'needs_review_ap'
  | 'unmatched_ap'
  | 'pending_approval'
  | 'budget_overrun'
  | 'lapsed_qualified_invoice'
  | 'close_blocker';

export type SignalAgent =
  | 'Anomaly Watcher'
  | 'Collections'
  | 'Matching'
  | 'Close Coach'
  | 'Tax-Credit Reconciler'
  | 'Cash-Forecast';

/**
 * A ranked "next best action" surfaced by the backend SignalsService (Wave 0).
 * The backend emits i18n *keys* + `params`; the frontend translates with
 * `t(titleKey, params)` / `t(rationaleKey, params)`. `ctaPath` already has the
 * workspace id substituted, so a CTA is a one-click deep link.
 */
export interface Signal {
  id: string;
  kind: SignalKind;
  severity: SignalSeverity;
  titleKey: string;
  rationaleKey: string;
  params: Record<string, string | number>;
  impactValue: number;
  impactUnit: 'JPY' | 'days' | 'count';
  ctaPath: string;
  ctaKey: string;
  entityRef?: { kind: string; id: string };
  agent: SignalAgent;
}

export interface SignalsSummary {
  high: number;
  medium: number;
  low: number;
  total: number;
}

/**
 * Ranked Next-Best-Action feed for a workspace. Polls every 60s (cheap,
 * deterministic backend) and also invalidates whenever the workspace's
 * realtime socket reports a domain change (new AP invoice, OCR done, approval
 * decided, job completed) so resolving the underlying record drops the signal
 * almost immediately rather than on the next poll.
 */
export function useSignals(wsId: string | undefined) {
  const qc = useQueryClient();

  const query = useQuery<Signal[]>({
    queryKey: ['signals', wsId],
    enabled: !!wsId,
    refetchInterval: 60_000,
    queryFn: async () => (await api.get<Signal[]>(`/workspaces/${wsId}/signals`)).data,
  });

  useEffect(() => {
    if (!wsId) return;
    const socket = getSocket();
    const invalidate = () => {
      qc.invalidateQueries({ queryKey: ['signals', wsId] });
      qc.invalidateQueries({ queryKey: ['signals-summary', wsId] });
    };
    socket.on('ap_invoice.changed', invalidate);
    socket.on('approval.changed', invalidate);
    socket.on('job.completed', invalidate);
    return () => {
      socket.off('ap_invoice.changed', invalidate);
      socket.off('approval.changed', invalidate);
      socket.off('job.completed', invalidate);
    };
  }, [wsId, qc]);

  return query;
}

/** Counts of open signals by severity, for the home KPI tiles. */
export function useSignalsSummary(wsId: string | undefined) {
  return useQuery<SignalsSummary>({
    queryKey: ['signals-summary', wsId],
    enabled: !!wsId,
    refetchInterval: 60_000,
    queryFn: async () => (await api.get<SignalsSummary>(`/workspaces/${wsId}/signals/summary`)).data,
  });
}

const SEVERITY_TONE: Record<SignalSeverity, 'warn' | 'accent' | 'muted'> = {
  high: 'warn',
  medium: 'accent',
  low: 'muted',
};

/** Map a signal severity to a `<Pill>` tone — shared by HomePage + Inbox. */
export function severityTone(severity: SignalSeverity): 'warn' | 'accent' | 'muted' {
  return SEVERITY_TONE[severity];
}
